import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

const MONTHS_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
// Padrão brasileiro: semana começa no domingo.
const DAYS_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export const today = () => new Date().toISOString().split('T')[0];
export const fmtFull = (d: string) => { if (!d) return '—'; const [y, m, day] = d.split('-'); return `${day}/${m}/${y}`; };
// Ancorado em meio-dia antes de somar/subtrair dias, pra não cair em problema de DST ao reformatar.
const shiftDate = (isoDate: string, days: number) => {
    const d = new Date(isoDate + 'T12:00:00');
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Atalhos rápidos (Ontem/Hoje/Amanhã) exibidos no topo do popup do DatePicker — aplicam na hora, sem precisar de "Aplicar". */
export const QuickDateShortcuts: React.FC<{ onPick: (dateStr: string) => void }> = ({ onPick }) => {
    const t = today();
    const shortcuts = [
        { label: 'Ontem', date: shiftDate(t, -1) },
        { label: 'Hoje', date: t },
        { label: 'Amanhã', date: shiftDate(t, 1) },
    ];
    return (
        <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-2 border-b border-gray-100 dark:border-gray-700">
            {shortcuts.map(s => (
                <button
                    key={s.label} type="button" onClick={() => onPick(s.date)}
                    className="px-2.5 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary/10 hover:text-primary transition-colors"
                >
                    {s.label}
                </button>
            ))}
        </div>
    );
};

export const CalendarMonth: React.FC<{
    year: number; month: number;
    tempStart: string; tempEnd: string; hoverDate: string;
    onSelect: (d: string) => void; onHover: (d: string) => void;
}> = ({ year, month, tempStart, tempEnd, hoverDate, onSelect, onHover }) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    // getDay() já retorna 0 = domingo, que é a primeira coluna do calendário (padrão brasileiro).
    const offset = new Date(year, month, 1).getDay();
    const todayStr = today();
    const rangeEnd = tempEnd || hoverDate;
    const [rangeS, rangeE] = tempStart && rangeEnd
        ? (tempStart <= rangeEnd ? [tempStart, rangeEnd] : [rangeEnd, tempStart])
        : ['', ''];
    const cells: (string | null)[] = Array(offset).fill(null);
    for (let d = 1; d <= daysInMonth; d++)
        cells.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);

    return (
        <div className="p-3 w-60">
            <p className="text-xs font-semibold text-center text-gray-700 dark:text-gray-200 mb-2">{MONTHS_PT[month]} {year}</p>
            <div className="grid grid-cols-7 mb-1">
                {DAYS_SHORT.map(d => <div key={d} className="text-center text-[10px] font-medium text-gray-400 py-0.5">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-y-0.5">
                {cells.map((dateStr, i) => {
                    if (!dateStr) return <div key={`e${i}`} className="h-7" />;
                    const isStart = dateStr === tempStart, isEnd = dateStr === (tempEnd || hoverDate);
                    const inRange = !!(rangeS && rangeE && dateStr > rangeS && dateStr < rangeE);
                    return (
                        <button key={dateStr} type="button" onMouseEnter={() => onHover(dateStr)} onClick={() => onSelect(dateStr)}
                            className={[
                                'h-7 w-7 text-xs font-medium flex items-center justify-center mx-auto transition-all select-none',
                                isStart || isEnd ? 'rounded-full bg-primary text-white shadow-sm' :
                                    inRange ? 'bg-primary/15 text-primary dark:text-green-300 rounded-none' :
                                        'rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300',
                                dateStr === todayStr && !isStart && !isEnd ? 'ring-1 ring-primary ring-offset-1' : ''
                            ].join(' ')}>
                            {parseInt(dateStr.split('-')[2])}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

/** Estilo do botão-gatilho compartilhado entre DateRangePicker e DatePicker para os três campos de data ficarem padronizados. */
export const DATE_TRIGGER_CLASS = 'w-full flex items-center gap-2 px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors';

/** Seletor de intervalo de datas com dois calendários lado a lado (estilo usado no módulo Marketing). */
const DateRangePicker: React.FC<{
    startDate: string; endDate: string;
    onChange: (s: string, e: string) => void;
    triggerLabel?: string;
    placeholder?: string;
    align?: 'left' | 'right';
}> = ({ startDate, endDate, onChange, triggerLabel, placeholder = 'Selecione o período', align = 'left' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempStart, setTempStart] = useState(startDate);
    const [tempEnd, setTempEnd] = useState(endDate);
    const [hoverDate, setHoverDate] = useState('');
    const [phase, setPhase] = useState<'start' | 'end'>('start');
    const [viewYear, setViewYear] = useState(() => new Date((startDate || today()) + 'T12:00:00').getFullYear());
    const [viewMonth, setViewMonth] = useState(() => new Date((startDate || today()) + 'T12:00:00').getMonth());
    const triggerRef = useRef<HTMLButtonElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    // Posição calculada via getBoundingClientRect + portal para document.body — o popup escapa de
    // qualquer ancestral com overflow:auto/hidden (ex.: o corpo rolável do modal de Filtros), que
    // antes cortava o calendário no CSS `position: absolute` local. Abre pra cima (`bottom`) quando
    // o campo está perto do fim da tela (a pedido do usuário, caso original) e pra baixo (`top`)
    // quando não há espaço acima — senão o popup fica cortado em campos no topo de um modal/página.
    const POPUP_ESTIMATED_HEIGHT = 420;
    const LARGURA_MES = 240; // w-60 do CalendarMonth
    const [coords, setCoords] = useState<{ top?: number; bottom?: number; left: number; doisMeses: boolean } | null>(null);

    useEffect(() => {
        const fn = (e: MouseEvent) => {
            const target = e.target as Node;
            if (triggerRef.current?.contains(target)) return;
            if (popupRef.current?.contains(target)) return;
            setIsOpen(false);
        };
        if (isOpen) document.addEventListener('mousedown', fn);
        return () => document.removeEventListener('mousedown', fn);
    }, [isOpen]);
    useEffect(() => { setTempStart(startDate); setTempEnd(endDate); }, [startDate, endDate]);

    useEffect(() => {
        if (!isOpen) return;
        const updateCoords = () => {
            const rect = triggerRef.current?.getBoundingClientRect();
            if (!rect) return;
            const spaceAbove = rect.top;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openDownward = spaceAbove < POPUP_ESTIMATED_HEIGHT && spaceBelow > spaceAbove;

            // Em tela estreita (celular) dois meses lado a lado (2×240px) não cabem —
            // cai pra um mês só. Sempre calcula em `left` (nunca `right` do CSS): assim
            // dá pra fixar dentro da viewport de verdade, mesmo quando o gatilho está
            // colado na borda direita (era o que cortava o popup, com só um mês
            // aparecendo e o cabeçalho "Clique para..." truncado).
            const doisMeses = window.innerWidth >= 600;
            const larguraPopup = (doisMeses ? 2 : 1) * LARGURA_MES + 2;
            const leftDesejado = align === 'right' ? rect.right - larguraPopup : rect.left;
            const leftMax = Math.max(8, window.innerWidth - larguraPopup - 8);
            const left = Math.min(Math.max(8, leftDesejado), leftMax);

            setCoords({
                top: openDownward ? rect.bottom + 8 : undefined,
                bottom: openDownward ? undefined : window.innerHeight - rect.top + 8,
                left,
                doisMeses,
            });
        };
        updateCoords();
        // scroll não borbulha — capture:true também escuta rolagem de ancestrais internos (ex.: o modal).
        window.addEventListener('scroll', updateCoords, true);
        window.addEventListener('resize', updateCoords);
        return () => {
            window.removeEventListener('scroll', updateCoords, true);
            window.removeEventListener('resize', updateCoords);
        };
    }, [isOpen]);

    const open = () => {
        const d = new Date((startDate || today()) + 'T12:00:00');
        setViewYear(d.getFullYear()); setViewMonth(d.getMonth());
        setTempStart(startDate); setTempEnd(endDate); setPhase('start'); setIsOpen(true);
    };
    const handleSelect = (dateStr: string) => {
        if (phase === 'start' || dateStr < tempStart) { setTempStart(dateStr); setTempEnd(''); setPhase('end'); }
        else { setTempEnd(dateStr); setPhase('start'); }
    };
    const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); };
    const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); };
    const month2 = viewMonth === 11 ? 0 : viewMonth + 1;
    const year2 = viewMonth === 11 ? viewYear + 1 : viewYear;
    const dayCount = tempStart && tempEnd
        ? Math.round((new Date(tempEnd + 'T12:00:00').getTime() - new Date(tempStart + 'T12:00:00').getTime()) / 86400000) + 1
        : null;

    return (
        <div className="relative">
            <button ref={triggerRef} type="button" onClick={open} className={triggerLabel
                ? 'text-[11px] font-semibold text-primary hover:underline'
                : DATE_TRIGGER_CLASS
            }>
                {triggerLabel ? triggerLabel : (
                    <>
                        <Calendar size={14} className="text-gray-400 flex-shrink-0" />
                        {startDate && endDate ? (
                            <>
                                <span className="font-semibold">{fmtFull(startDate)}</span>
                                <span className="text-gray-400">→</span>
                                <span className="font-semibold">{fmtFull(endDate)}</span>
                            </>
                        ) : (
                            <span className="text-gray-400">{placeholder}</span>
                        )}
                    </>
                )}
            </button>
            {isOpen && coords && createPortal(
                <div
                    ref={popupRef}
                    style={{ position: 'fixed', ...(coords.top !== undefined ? { top: coords.top } : { bottom: coords.bottom }), left: coords.left }}
                    className="z-[10050] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl"
                >
                    <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                        <button type="button" onClick={prevMonth} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><ChevronLeft size={15} /></button>
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{phase === 'start' ? 'Clique para definir o início' : 'Clique para definir o fim'}</span>
                        <button type="button" onClick={nextMonth} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><ChevronRight size={15} /></button>
                    </div>
                    <div className={`flex ${coords.doisMeses ? 'divide-x divide-gray-100 dark:divide-gray-700' : ''}`} onMouseLeave={() => setHoverDate('')}>
                        <CalendarMonth year={viewYear} month={viewMonth} tempStart={tempStart} tempEnd={tempEnd} hoverDate={hoverDate} onSelect={handleSelect} onHover={setHoverDate} />
                        {coords.doisMeses && (
                            <CalendarMonth year={year2} month={month2} tempStart={tempStart} tempEnd={tempEnd} hoverDate={hoverDate} onSelect={handleSelect} onHover={setHoverDate} />
                        )}
                    </div>
                    <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-gray-700">
                        <span className="text-xs text-gray-500">{dayCount ? `${dayCount} dia${dayCount > 1 ? 's' : ''} selecionado${dayCount > 1 ? 's' : ''}` : 'Selecione o período'}</span>
                        <div className="flex gap-2">
                            <button type="button" onClick={() => { setTempStart(''); setTempEnd(''); setPhase('start'); onChange('', ''); }} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Limpar</button>
                            <button type="button" onClick={() => { if (tempStart && tempEnd) { onChange(tempStart, tempEnd); setIsOpen(false); } }} disabled={!tempStart || !tempEnd} className="px-3 py-1.5 text-xs font-semibold bg-primary text-white rounded-lg disabled:opacity-40 hover:bg-primary/90">Aplicar</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default DateRangePicker;
